import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, TrendingUp, TrendingDown, Loader2, Plus, RefreshCw, X,
  Star, Inbox, Building2, ChevronRight,
} from "lucide-react";
import {
  locationsRollup, locationRollupDetail, locationCreate,
  locationPatch, locationDelete,
} from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * LocationsRollupPage — /dashboard/locations
 *
 * Read-only rollup so franchise / multi-branch owners see every outpost at
 * a glance. Each row: LV score, unread inbox, avg rating, weekly leads,
 * composite health tile. Click a row to see the top 10 recent inbox
 * threads + reviews for that location.
 */
const TONE = {
  good: "border-emerald-300 bg-emerald-50 text-emerald-800",
  watch: "border-amber-300 bg-amber-50 text-amber-800",
  critical: "border-rose-300 bg-rose-50 text-rose-800",
  unknown: "border-slate-300 bg-slate-50 text-slate-500",
};

export default function LocationsRollupPage() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newLoc, setNewLoc] = useState({ name: "", city: "", email_hint: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setData(await locationsRollup());
    } catch {
      toast.error("Sign in to view locations");
    }
  };
  useEffect(() => {
    document.title = "Locations — WebsiteTech.pro";
    load();
  }, []);

  const openDetail = async (lid) => {
    try {
      setDetail(await locationRollupDetail(lid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load");
    }
  };

  const addLocation = async () => {
    if (!newLoc.name.trim()) { toast.error("Give the location a name"); return; }
    setBusy(true);
    try {
      await locationCreate(newLoc);
      toast.success("Location added");
      setNewLoc({ name: "", city: "", email_hint: "" });
      setShowAdd(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add");
    } finally { setBusy(false); }
  };

  const removeLocation = async (lid) => {
    if (!confirm("Delete this location? Contacts and history stay with the account.")) return;
    try {
      await locationDelete(lid);
      toast.success("Deleted");
      setDetail(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="locations-page">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Building2 className="h-4 w-4" /> Multi-location rollup
          </div>
          <h1 className="mt-1 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            All your branches in one glance
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Every location's search visibility, unread leads, and reputation
            score, side by side. Click any row to see recent conversations
            and reviews for that branch.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-6">
        {/* Owner-wide top-line */}
        {data && (
          <div className="grid gap-3 sm:grid-cols-4">
            <TopStat label="Locations" value={data.totals.count} />
            <TopStat label="Avg health" value={data.totals.avg_health ?? "—"} suffix="/100" />
            <TopStat label="Unread leads" value={data.totals.inbox_unread}
              tone={data.totals.inbox_unread > 0 ? "watch" : "good"} />
            <TopStat label="New leads · 7d" value={data.totals.leads_last_7d} />
          </div>
        )}
        {data && data.totals.avg_health === null && (
          <p className="mt-2 text-xs italic text-slate-500" data-testid="loc-health-hint">
            Health scores appear once you've captured your first LV audit,
            SEO scan, or review for a location.
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={load} data-testid="loc-refresh">
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
          <Button className="bg-orange-600 text-white hover:bg-orange-700"
            onClick={() => setShowAdd(true)} data-testid="loc-add">
            <Plus className="mr-1 h-4 w-4" /> Add location
          </Button>
        </div>

        {!data ? (
          <div className="mt-6 flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <Card className="mt-4 overflow-hidden border border-slate-200 bg-white" data-testid="loc-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Location</th>
                  <th className="px-3 py-2 text-right">Health</th>
                  <th className="px-3 py-2 text-right">Visibility</th>
                  <th className="px-3 py-2 text-right">Unread</th>
                  <th className="px-3 py-2 text-right">New · 7d</th>
                  <th className="px-3 py-2 text-right">Rating</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.locations.map((l) => (
                  <tr key={l.id || "main"}
                    onClick={() => l.id && openDetail(l.id)}
                    className={l.id ? "cursor-pointer hover:bg-slate-50" : ""}
                    data-testid={`loc-row-${l.id || "main"}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-900">{l.name}</div>
                          {l.city && <div className="text-xs text-slate-500">{l.city}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Badge variant="outline" className={TONE[l.health_tone]}>
                        {l.health_score ?? "—"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {l.local_visibility.score ?? "—"}/100
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={l.inbox.unread > 0 ? "font-bold text-rose-700" : "text-slate-500"}>
                        {l.inbox.unread}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {l.inbox.new_7d}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {l.reputation.avg_rating ? (
                        <span><Star className="mr-0.5 inline h-3 w-3 fill-amber-400 text-amber-400" />{l.reputation.avg_rating}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {l.id && <ChevronRight className="inline h-4 w-4 text-slate-400" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.locations.length === 1 && !data.locations[0].id && (
              <div className="border-t border-slate-100 bg-slate-50 p-4 text-xs text-slate-500">
                You haven't added any locations yet. Click <b>Add location</b>{" "}
                to start tracking each branch separately.
              </div>
            )}
          </Card>
        )}
      </section>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setDetail(null)}
          data-testid="loc-detail-modal">
          <Card className="max-h-[85vh] w-full max-w-3xl overflow-y-auto border border-slate-200 bg-white p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Location</div>
                <h2 className="mt-1 font-heading text-2xl font-bold text-slate-900">{detail.location.name}</h2>
                <div className="mt-1 text-sm text-slate-600">
                  {detail.location.city} · verification: {detail.location.verification_status}
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="rounded p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <MiniStat label="Health" v={detail.location.health_score ?? "—"} tone={detail.location.health_tone} />
              <MiniStat label="Local visibility" v={`${detail.location.local_visibility.score ?? "—"}/100`}
                tone={detail.location.local_visibility.tone} />
              <MiniStat label="Rating" v={detail.location.reputation.avg_rating
                ? `${detail.location.reputation.avg_rating}★`
                : "—"} tone="unknown" />
            </div>

            <h3 className="mt-6 text-xs font-semibold uppercase tracking-widest text-slate-500">
              Recent inbox
            </h3>
            {detail.recent_inbox.length === 0 ? (
              <div className="mt-2 rounded-md border border-dashed border-slate-300 p-4 text-xs text-slate-500">
                No conversations tagged to this location yet.{" "}
                Emails sent to <code>inbox+SLUG+{detail.location.id.slice(0,8)}@…</code> will land here.
              </div>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200">
                {detail.recent_inbox.map((t) => (
                  <li key={t.id} className="px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t.subject}</span>
                      <span className="text-xs text-slate-500">{t.channel}</span>
                    </div>
                    <div className="text-xs text-slate-500">{t.contact}</div>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mt-6 text-xs font-semibold uppercase tracking-widest text-slate-500">
              Recent reviews
            </h3>
            {detail.recent_reviews.length === 0 ? (
              <div className="mt-2 rounded-md border border-dashed border-slate-300 p-4 text-xs text-slate-500">
                No reviews tagged to this location yet.
              </div>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200">
                {detail.recent_reviews.map((r) => (
                  <li key={r.id} className="px-3 py-2 text-sm">
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {r.rating}
                      <span className="ml-2 font-medium">{r.reviewer || "Anonymous"}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-600">{r.excerpt}</div>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-6 flex justify-between border-t border-slate-100 pt-4">
              <Button variant="outline" onClick={() => removeLocation(detail.location.id)}
                className="text-rose-600" data-testid="loc-delete">
                Delete location
              </Button>
              <Button asChild variant="outline">
                <Link to="/inbox">Open Unified Inbox</Link>
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setShowAdd(false)}
          data-testid="loc-add-modal">
          <Card className="w-full max-w-md border border-slate-200 bg-white p-5"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold text-slate-900">Add a location</h3>
            <p className="mt-1 text-xs text-slate-500">
              Give it a friendly name. If you have multiple branches, set an
              email hint like "downtown" — customers who email{" "}
              <code>inbox+slug+downtown@…</code> will land in this branch.
            </p>
            <div className="mt-3 space-y-3">
              <F label="Name (required)" v={newLoc.name}
                 onChange={(v) => setNewLoc({ ...newLoc, name: v })}
                 testid="loc-name" placeholder="e.g. Downtown Store" />
              <F label="City" v={newLoc.city}
                 onChange={(v) => setNewLoc({ ...newLoc, city: v })}
                 testid="loc-city" />
              <F label="Email hint (short slug)" v={newLoc.email_hint}
                 onChange={(v) => setNewLoc({ ...newLoc, email_hint: v.toLowerCase().replace(/[^a-z0-9\-]/g,"") })}
                 testid="loc-hint" placeholder="downtown" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button className="bg-orange-600 text-white hover:bg-orange-700"
                onClick={addLocation} disabled={busy}
                data-testid="loc-add-submit">Add</Button>
            </div>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
}

function TopStat({ label, value, suffix = "", tone }) {
  const cls = tone === "watch" ? "text-amber-800" :
              tone === "critical" ? "text-rose-800" :
              tone === "good" ? "text-emerald-800" : "text-slate-900";
  return (
    <Card className="border border-slate-200 bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-black ${cls}`}>
        {value}<span className="text-sm font-normal text-slate-400">{suffix}</span>
      </div>
    </Card>
  );
}

function MiniStat({ label, v, tone }) {
  return (
    <div className={`rounded-md border p-3 ${TONE[tone]}`}>
      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80">{label}</div>
      <div className="mt-0.5 font-heading text-lg font-black">{v}</div>
    </div>
  );
}

function F({ label, v, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500">{label}</label>
      <input type="text" value={v || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        data-testid={testid} />
    </div>
  );
}
